import type { IImportColumn, Importer } from '@feathr/blackbox';
import { isWaitingForColumn } from '@feathr/blackbox';

import { getUnusedColumns } from './DataImportStepTwo.utils';

/** Props every mapping-table cell takes. */
export interface IImportMappingCellProps {
  column: IImportColumn;
  importer: Importer;
  disabled: boolean;
}

/** A mapping row as the table sees it: the mapper, under an id unique to it. */
export interface IImportMappingRow {
  id: string;
  column: IImportColumn;
}

/*
 * Ids by mapper object. Two mappers can name the same file column, so the column name can't key a
 * row; the mapper object itself is stable for as long as the row exists, so it is the identity.
 */
const rowIds = new WeakMap<IImportColumn, string>();
let nextRowId = 0;

function getRowId(column: IImportColumn): string {
  let id = rowIds.get(column);
  if (id === undefined) {
    nextRowId += 1;
    id = `mapper-${nextRowId}`;
    rowIds.set(column, id);
  }
  return id;
}

/** The mapping rows to show: every mapper with a column, each under its own stable id. */
export function toMappingRows(columnMappers: IImportColumn[]): IImportMappingRow[] {
  return columnMappers
    .filter((column) => column.column_name !== '')
    .map((column) => ({ id: getRowId(column), column }));
}

/** Whether the file has a column left for one more row, once the rows waiting for one take theirs. */
export function hasColumnForNewRow(columnNames: string[], columnMappers: IImportColumn[]): boolean {
  const waiting = columnMappers.filter((column) => isWaitingForColumn(column)).length;
  return getUnusedColumns(columnNames, columnMappers).length > waiting;
}
